// CourseDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import noimage from "../asset/noimage.png";
import { MdCheckCircle, MdDownload } from "react-icons/md";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaMapMarkerAlt, FaEnvelope, FaPhone } from "react-icons/fa";
import { BsPersonCheckFill } from "react-icons/bs";
import instructorImage from "../asset/person.png";
import certificateImage from "../asset/Certificate.png";
import Swal from "sweetalert2";
import { FaArrowLeft } from "react-icons/fa";

import coursedetailsbg from "../asset/coursedetails-bg.png";



const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [decodedId, setDecodedId] = useState(null);
  const [course, setCourse] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [topics, setTopics] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [tools, setTools] = useState([]);
  const [instructor, setInstructor] = useState(null);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [instructorSlots, setInstructorSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
const [instructors, setInstructors] = useState([]);
const [selectedInstructor, setSelectedInstructor] = useState(null);
const [instructorSlotsAll, setInstructorSlotsAll] = useState([]); // All slots from all instructors

  const openCerticateModal = () => setIsCertificateModalOpen(true);
  const closeCertificateModal = () => setIsCertificateModalOpen(false);
  const openPreviewModal = () => setIsPreviewModalOpen(true);
  const closePreviewModal = () => setIsPreviewModalOpen(false);

  /* ------------------------------------------------------------------ */
  /* 1. Decode the Base64 id                                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    try {
      const decoded = atob(id);
      setDecodedId(decoded);
    } catch (err) {
      console.error("Failed to decode course id:", err);
    }
  }, [id]);

  /* ------------------------------------------------------------------ */
  /* 2. Fetch the course data                                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!decodedId) return;

    fetch(SummaryApi.getActiveCourses.url)
      .then((res) => res.json())
      .then((data) => {
        const found = data.data.find((c) => String(c.course_id) === decodedId);
        if (found) setCourse(found);
      })
      .catch((err) => console.error("Failed to fetch course:", err));
  }, [decodedId]);

/* ------------------------------------------------------------------ */
/* 3. Check enrollment status - API already filters by course        */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* 3. Check enrollment status - Filter by course_id on frontend      */
/* ------------------------------------------------------------------ */
useEffect(() => {
  const checkEnrollmentStatus = async () => {
    const studentId = localStorage.getItem("student_id");
    if (!studentId || !course) return;

    try {
      const enrollmentRes = await fetch(
        SummaryApi.enrollment_retrieve_by_student_and_course.url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            student_id: Number(studentId),
          }),
        }
      );

      const enrollmentData = await enrollmentRes.json();
      
      // Filter enrollments to find ONLY the one for this specific course
      if (enrollmentData.status && enrollmentData.data?.length > 0) {
        const enrollmentForThisCourse = enrollmentData.data.find(
          enrollment => enrollment.course_id === course.course_id
        );
        
        setIsEnrolled(!!enrollmentForThisCourse);
      } else {
        setIsEnrolled(false);
      }
    } catch (error) {
      console.error("Error checking enrollment status:", error);
      setIsEnrolled(false);
    }
  };

  checkEnrollmentStatus();
}, [course]);
  /* ------------------------------------------------------------------ */
  /* 4. Load curriculum, lessons, FAQs, tools, instructor               */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!course) return;

    const fetchCurriculumAndLessons = async () => {
      try {
        // Curriculum
        const curriculumRes = await fetch(
          SummaryApi.getCurriculumByCourseId.url,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ course_id: course.course_id }),
          }
        );
        const curriculumData = await curriculumRes.json();

        // Lessons
        const lessonRes = await fetch(SummaryApi.getLessonByCourseId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: course.course_id }),
        });
        const lessonData = await lessonRes.json();

        if (curriculumData.status) {
          const groupedLessons = (lessonData.status ? lessonData.data : []).reduce(
            (acc, lesson) => {
              if (!acc[lesson.curriculum_id]) acc[lesson.curriculum_id] = [];
              acc[lesson.curriculum_id].push(lesson);
              return acc;
            },
            {}
          );

          // === NEW: Calculate total duration per topic ===
          const calculateTotalDuration = (lessons) => {
            return lessons.reduce((sum, l) => sum + (parseInt(l.duration) || 0), 0);
          };

          const structuredTopics = curriculumData.data.map((curr, index) => {
            const lessons = groupedLessons[curr.curriculum_id] || [];
            const totalDuration = calculateTotalDuration(lessons);

            return {
              id: curr.curriculum_id,
              title: curr.topic_name,
              topicDuration: curr.duration,
              lessons: lessons,
              isOpen: index === 0,
              totalDuration: totalDuration, // Auto-calculated
            };
          });
          // === END NEW ===

          setTopics(structuredTopics);
        }

        // FAQs
        const faqRes = await fetch(SummaryApi.getFaqsByCourseID.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: course.course_id }),
        });
        const faqData = await faqRes.json();
        if (faqData.status) setFaqs(faqData.data);

        // Tools
        const toolRes = await fetch(SummaryApi.getToolsByCourseId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: course.course_id }),
        });
        const toolData = await toolRes.json();
        if (toolData.status) setTools(toolData.data);


      // Instructor - handle multiple instructors
      const instructorRes = await fetch(SummaryApi.instructorByCourseId.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: course.course_id }),
      });
      const instructorData = await instructorRes.json();

// In the instructor fetch section, update to set the default instructor
if (instructorData.status && Array.isArray(instructorData.data) && instructorData.data.length > 0) {
  setInstructors(instructorData.data);
  // Set the first instructor as selected by default for modal
  setSelectedInstructor(instructorData.data[0]);
  // Set the default instructor for the tab display (using selectBestInstructor or first one)
  setInstructor(selectBestInstructor(instructorData.data));
} else {
  console.warn("No instructor data found for this course");
}
    } catch (err) {
      console.error("Failed to fetch curriculum/lessons/faqs/tools:", err);
    }
  };

  fetchCurriculumAndLessons();
}, [course]);
  /* ------------------------------------------------------------------ */
  /* Helper functions                                                   */
  /* ------------------------------------------------------------------ */
  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const parseSkills = (htmlString) => {
    const temp = document.createElement("div");
    temp.innerHTML = htmlString;
    const listItems = temp.querySelectorAll("li");
    return Array.from(listItems).map((li) => li.textContent.trim());
  };

  const parseStudentLearning = (htmlString) => {
    const temp = document.createElement("div");
    temp.innerHTML = htmlString;
    const listItems = temp.querySelectorAll("li");

    if (listItems.length > 0) {
      return Array.from(listItems).map((li) => li.textContent.trim());
    } else {
      const textContent = temp.textContent.trim();
      if (textContent) {
        const sentences = textContent.split(/(?<=[.?!])\s+/);
        return sentences.filter((s) => s.length > 0);
      }
      return [];
    }
  };

  const toggleTopic = (idToToggle) => {
    setTopics((prev) =>
      prev.map((topic) => ({
        ...topic,
        isOpen: topic.id === idToToggle ? !topic.isOpen : false,
      }))
    );
  };

  const selectBestInstructor = (instructors) => {
    if (instructors.length === 1) return instructors[0];

    return instructors
      .slice()
      .sort((a, b) => {
        const ratingA = parseFloat(a.rating) || 0;
        const ratingB = parseFloat(b.rating) || 0;

        if (ratingB > ratingA) return 1;
        if (ratingB < ratingA) return -1;

        return new Date(a.created_date) - new Date(b.created_date);
      })[0];
  };

  /* ------------------------------------------------------------------ */
  /* Enrollment flow                                                    */
  /* ------------------------------------------------------------------ */
 const handleEnrollClick = async () => {
  if (isEnrolled) {
    Swal.fire({
      icon: "info",
      title: "Already Enrolled",
      text: "You are already enrolled in this course.",
      confirmButtonColor: "#3085d6",
      confirmButtonText: "OK",
    });
    return;
  }

  const studentId = localStorage.getItem("student_id");
  if (!studentId) {
    Swal.fire({
      icon: "warning",
      title: "Please Login",
      text: "You need to login as a student before enrolling.",
      confirmButtonColor: "#3085d6",
      confirmButtonText: "OK",
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/student-login", { replace: true });
      }
    });
    return;
  }

  try {
    const res = await fetch(SummaryApi.getTimeslotsByCourseId.url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ course_id: course.course_id }),
    });

    const data = await res.json();

    if (data.status && Array.isArray(data.data)) {
      const formattedSlots = data.data.map((slot) => ({
        ...slot,
        date: new Date(slot.start_date).toLocaleDateString(),
        time: `${formatTime(slot.start_time)} - ${formatTime(slot.end_time)}`,
        slot_type: slot.batch_name,
      }));
      setInstructorSlotsAll(formattedSlots);
      
      // Set slots for the initially selected instructor
      if (selectedInstructor) {
        const initialSlots = formattedSlots.filter(
          slot => slot.instructor_id === selectedInstructor.instructor_id
        );
        setInstructorSlots(initialSlots);
      }
      
      setSelectedSlot(null);
    } else {
      setInstructorSlotsAll([]);
      setInstructorSlots([]);
    }

    setIsSlotModalOpen(true);
  } catch (error) {
    console.error("Error fetching slots:", error);
  }
};

  const handleSlotSelection = (slot) => {
    setSelectedSlot(slot);
  };

  const handleConfirmSlot = async () => {
    if (!selectedSlot) {
      Swal.fire({
        icon: "warning",
        title: "No Slot Selected",
        text: "Please select an available slot before confirming.",
        confirmButtonText: "OK",
      });
      return;
    }

    const studentId = localStorage.getItem("student_id");
    if (!studentId) {
      Swal.fire({
        icon: "error",
        title: "Enrollment Failed",
        text: "Student ID not found. Please log in.",
        confirmButtonText: "OK",
      });
      return;
    }

    const payload = {
      student_id: Number(studentId),
      course_id: Number(course.course_id),
      instructor_id: Number(selectedSlot.instructor_id),
      time_slot_id: selectedSlot.time_slot_id,
      time_slot: `${selectedSlot.date}, ${selectedSlot.time} (${selectedSlot.slot_type})`,
      enrollment_date: new Date().toISOString(),
    };

    try {
      const enrollmentRes = await fetch(
        SummaryApi.assignCourseToStudent.url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const enrollmentData = await enrollmentRes.json();
  const formatDMY = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };
      if (enrollmentData.status) {
              setIsEnrolled(true);
           Swal.fire({
          icon: "success",
          title: "Enrollment Confirmed!",
          html: `You have successfully enrolled in:<br><strong>${course.course_title}</strong><br>For the slot: <strong>${formatDMY(selectedSlot.start_date)} - ${selectedSlot.time} (${selectedSlot.batch_name})</strong>`,
          confirmButtonText: "Proceed to Payment",
        }).then(() => {
          setIsSlotModalOpen(false);
          setIsPaymentModalOpen(true);
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Enrollment Failed",
          text:
            enrollmentData.message ||
            "Something went wrong during enrollment.",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error creating enrollment:", error);
      Swal.fire({
        icon: "error",
        title: "Enrollment Error",
        text: "Could not complete enrollment due to a network or server issue.",
        confirmButtonText: "OK",
      });
    }
  };

  const handlePaymentAndNavigate = () => {
    Swal.fire({
      icon: "success",
      title: "Payment Successful!",
      text: "You have successfully completed your payment and enrollment.",
      confirmButtonText: "Go to Dashboard",
    }).then(() => {
      setIsPaymentModalOpen(false);
      navigate("/student-panel/student-dashboard");
    });
  };
// Prevent background scroll when any modal is open
useEffect(() => {
  if (isSlotModalOpen || isPaymentModalOpen || isCertificateModalOpen || isPreviewModalOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "unset";
  }

  // Cleanup on unmount or when modals close
  return () => {
    document.body.style.overflow = "unset";
  };
}, [isSlotModalOpen, isPaymentModalOpen, isCertificateModalOpen, isPreviewModalOpen]);
  if (!course) {
    return (
      <div className="p-10 text-center text-gray-600 dark:text-gray-300">
        Loading course details...
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* UI Components                                                      */
  /* ------------------------------------------------------------------ */
  const FeatureItem = ({ text }) => (
    <div className="flex items-start gap-2">
      <MdCheckCircle className="text-green-500 dark:text-green-400 w-6 h-5 flex-shrink-0 mt-1" />
      <span className="leading-tight text-gray-700 dark:text-gray-300">{text}</span>
    </div>
  );
// Handle Instructor Dropdown Change
  const handleInstructorChange = (e) => {
    const selectedId = Number(e.target.value);
    const selectedInst = instructors.find(
      (inst) => inst.instructor_id === selectedId
    );
    if (selectedInst) {
      setInstructor(selectedInst);
    }
  };

 return (
  <div className="bg-white dark:bg-gray-900 min-h-screen">
    {/* Hero Section */}
    {/* Back Button - Visible only on Mobile & Tablet (hidden on lg+) */}
<button
  onClick={() => window.history.back()}
  className="fixed top-20 left-4 z-50 flex items-center justify-center w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all border border-gray-200 dark:border-gray-700 lg:hidden"
  aria-label="Go back"
>
  <FaArrowLeft className="text-2xl text-[#103b6b] dark:text-white" />
</button>
<section
  className="text-white flex flex-col md:flex-row items-center justify-between px-4 sm:px-6 md:px-10 rounded-lg"
  style={{
    backgroundImage: `url(${coursedetailsbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  }}
>
  <div className="max-w-lg space-y-4 md:space-y-4 w-full backdrop-blur-[2px]  p-4 sm:p-6 rounded-lg">
    <button className="bg-[#5472B4] dark:bg-blue-700 px-4 py-1 rounded text-sm font-medium w-auto">
      Post Graduation Program
    </button>
    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
      {course.course_title}
    </h1>
    <p className="text-[#B1BFCB] dark:text-gray-300 text-base sm:text-lg">
      {course.subtitle ||
        "Master the latest technologies with our comprehensive certification program designed by industry experts."}
    </p>
    <div className="flex items-center space-x-4 sm:space-x-6 text-yellow-400 font-semibold flex-wrap">
      <div className="flex items-center space-x-1">
        <span className="text-xl sm:text-2xl">★</span>
        <span className="text-base sm:text-lg text-yellow-400">
          {course.rating || "4.5"}
        </span>
        <span className="text-gray-300 dark:text-gray-400 text-sm sm:text-base">(8542)</span>
      </div>
      <div className="flex items-center space-x-1 text-yellow-400">
        <BsPersonCheckFill />
        <span className="font-bold text-sm sm:text-base">10,000+ Enrolled</span>
      </div>
    </div>
    <div className="flex flex-row gap-3 sm:gap-4">
      <button
        className={`font-semibold px-4 sm:px-6 py-3 rounded shadow-md transition flex-1 sm:flex-none ${
          isEnrolled
            ? "bg-gray-400 text-gray-700 cursor-not-allowed"
            : "bg-white text-[#0F3557] hover:bg-gray-200"
        }`}
        onClick={handleEnrollClick}
        disabled={isEnrolled}
      >
        {isEnrolled ? "Enrolled" : "Enroll Now"}
      </button>
      <button
        className="border border-white px-4 sm:px-6 py-3 rounded font-semibold hover:bg-white hover:text-[#0F3557] transition flex-1 sm:flex-none"
        onClick={openPreviewModal}
      >
        Preview Course
      </button>
    </div>
  </div>

  <div className="mt-6 md:mt-0 w-full md:w-auto">
    <img
      src={
        course.course_thumbnail
          ? `${SummaryApi.Images.url.replace("api", "")}${course.course_thumbnail.replace(
              "/api",
              ""
            )}`
          : noimage
      }
      alt={course.course_title}
      className="w-full h-48 sm:h-56 md:h-64 mt-2 object-cover rounded-lg mb-2"
    />
  </div>
</section>
    {/* Main Content */}
    <div className="bg-gray-100 dark:bg-gray-800 p-4 sm:p-6 md:p-8 flex justify-center items-start">
      <div className="max-w-8xl w-full flex flex-col md:flex-row gap-6 md:gap-10">
        {/* Tabs Section */}
        <div className="flex-1 bg-white dark:bg-gray-700 rounded-lg shadow  sm:p-2 md:p-4">
<div className="mb-6 md:mb-8">
  {/* Hide scrollbar on md and above, show on small screens */}
  <div className="md:hidden overflow-x-auto scrollbar-hide">
    <div className="flex w-max border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
      {["overview", "curriculum", "instructor", "review"].map((tab) => (
        <button
          key={tab}
          className={`flex-shrink-0 py-3 px-6 text-sm font-medium transition ${
            activeTab === tab
              ? "bg-[#0F3557] dark:bg-blue-600 text-white"
              : "bg-white dark:bg-blue-700 text-gray-600 dark:text-blue-300 hover:bg-gray-100 dark:hover:bg-blue-600"
          }`}
          onClick={() => setActiveTab(tab)}
        >
          {tab.charAt(0).toUpperCase() + tab.slice(1)}
        </button>
      ))}
    </div>
  </div>

  {/* Full width tabs on medium screens and above */}
  <div className="hidden md:flex border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden w-full">
    {["overview", "curriculum", "instructor", "review"].map((tab) => (
      <button
        key={tab}
        className={`flex-1 py-3 px-4 text-base font-medium transition ${
          activeTab === tab
            ? "bg-[#0F3557] dark:bg-blue-600 text-white"
            : "bg-white dark:bg-blue-700 text-gray-600 dark:text-blue-300 hover:bg-gray-100 dark:hover:bg-blue-600"
        }`}
        onClick={() => setActiveTab(tab)}
      >
        {tab.charAt(0).toUpperCase() + tab.slice(1)}
      </button>
    ))}
  </div>
</div>  {/* Tab Content */}
          <div>
            {/* OVERVIEW */}
          {activeTab === "overview" && (
  <div className="max-w-8xl w-full">
    <h2 className="bg-[#0F3557] dark:bg-blue-700 text-white font-bold text-lg sm:text-xl rounded-t-md p-3 sm:p-4 mb-3 sm:mb-4">
      Course Overview
    </h2>
    <p className="text-gray-700 dark:text-white mb-4 sm:mb-6 leading-relaxed max-w-8xl p-2 text-sm sm:text-base">
      {course.description}
    </p>

    <div className="flex flex-col lg:flex-row justify-between gap-6 sm:gap-8">
      <div className="flex-1">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-3 sm:mb-5 text-base sm:text-lg">
          What you'll learn
        </h3>
        <div className="grid grid-cols-1 gap-y-4 sm:gap-y-6 gap-x-4 sm:gap-x-10 max-w-xl text-black-700 dark:text-gray-300">
          {parseStudentLearning(course.student_learning).map((item, idx) => (
            <FeatureItem key={idx} text={item} />
          ))}
        </div>
      </div>

      <div className="flex-1">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-3 sm:mb-5 text-base sm:text-lg">
          Requirements
        </h3>
        <div className="grid grid-cols-1 gap-y-4 sm:gap-y-6 gap-x-4 sm:gap-x-10 max-w-xl text-black-700 dark:text-gray-300">
          {parseStudentLearning(course.learning_requirements).map((item, idx) => (
            <FeatureItem key={idx} text={item} />
          ))}
        </div>
      </div>
    </div>

    {/* Tools */}
    {tools.length > 0 && (
      <div className="mt-6 sm:mt-10">
        <h3 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-4">
          Tools You'll Use
        </h3>
        <div className="flex flex-wrap gap-2 sm:gap-4">
          {tools.map((tool) => (
            <img
              key={tool.tools_id}
              src={
                tool.tool_image
                  ? `${SummaryApi.Images.url.replace(
                      "api",
                      ""
                    )}${tool.tool_image.replace("/api", "")}`
                  : noimage
              }
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = noimage;
              }}
              alt="Tool"
              className="h-12 sm:h-16 w-auto object-contain border p-1 sm:p-2 bg-white dark:bg-gray-600 rounded shadow"
            />
          ))}
        </div>
      </div>
    )}

    {/* FAQs */}
    {faqs.length > 0 && (
      <div className="mt-6 sm:mt-10">
        <h3 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-4">
          Frequently Asked Questions
        </h3>
        <div className="space-y-2 sm:space-y-3">
          {(showAllFaqs ? faqs : faqs.slice(0, 3)).map((faq) => {
            const isOpen = openFaqId === faq.information_id;
            return (
              <div
                key={faq.information_id}
                className="border border-gray-300 dark:border-gray-600 rounded-md p-3 sm:p-4 cursor-pointer bg-white dark:bg-gray-700"
                onClick={() =>
                  setOpenFaqId((prev) =>
                    prev === faq.information_id ? null : faq.information_id
                  )
                }
              >
                <div className="flex justify-between items-center font-semibold text-blue-800 dark:text-blue-300 text-sm sm:text-base">
                  <span>{faq.question}</span>
                  {isOpen ? (
                    <FiChevronUp className="text-lg sm:text-xl" />
                  ) : (
                    <FiChevronDown className="text-lg sm:text-xl" />
                  )}
                </div>
                {isOpen && (
                  <div className="mt-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {faqs.length > 3 && (
          <button
            onClick={() => setShowAllFaqs((prev) => !prev)}
            className="mt-3 sm:mt-4 text-blue-600 dark:text-blue-400 font-medium hover:underline text-sm sm:text-base"
          >
            {showAllFaqs ? "View Less" : "View More"}
          </button>
        )}
      </div>
    )}
  </div>
)}


            {/* CURRICULUM */}
            {activeTab === "curriculum" && (
              <div>
                <h2 className="bg-[#0F3557] dark:bg-gray-700 text-white font-bold text-lg sm:text-xl rounded-t-md p-3 sm:p-4 mb-3 sm:mb-4">
                  Course Curriculum
                </h2>
                {topics.length === 0 ? (
                  <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">No curriculum available.</p>
                ) : (
                  topics.map((topic) => (
                    <div key={topic.id} className="mb-3 sm:mb-4 border dark:border-gray-600 rounded">
                      <div
                        className="flex justify-between items-center bg-gray-100 dark:bg-gray-700 px-3 sm:px-4 py-2 sm:py-3 cursor-pointer"
                        onClick={() => toggleTopic(topic.id)}
                      >
                        <h3 className="font-semibold text-gray-800 dark:text-white text-sm sm:text-base">
                          {topic.title}
                          {/* === CHANGED: Show auto-calculated duration === */}
                          <span className="ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                            ({topic.totalDuration} mins)
                          </span>
                        </h3>
                        {topic.isOpen ? (
                          <FiChevronUp className="text-lg sm:text-xl text-gray-600 dark:text-gray-300" />
                        ) : (
                          <FiChevronDown className="text-lg sm:text-xl text-gray-600 dark:text-gray-300" />
                        )}
                      </div>
                      {topic.isOpen && (
                        <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 space-y-2">
                          {topic.lessons.length > 0 ? (
                            topic.lessons.map((lesson) => (
                              <div
                                key={lesson.lesson_id}
                                className="flex justify-between items-center bg-green-50 dark:bg-green-900/30 p-2 rounded"
                              >
                                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm sm:text-base">
                                  <div className="h-2 w-2 sm:h-3 sm:w-3 bg-green-500 dark:bg-green-400 rounded-full" />
                                  <span>{lesson.lesson_name}</span>
                                </div>
                                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                  {lesson.duration} mins
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">
                              No lessons found for this topic.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

        {activeTab === "instructor" && (
  <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden">
    <h2 className="bg-[#0F3557] dark:bg-blue-800 text-white font-bold text-lg sm:text-xl rounded-t-md p-3 sm:p-4 mb-3 sm:mb-4">
      Meet Your Instructor
    </h2>

    <div className="p-3 sm:p-4 pt-0">
      {/* INSTRUCTOR DROPDOWN - Aligned to Top Right */}
      {instructors.length > 0 && (
        <div className="mb-6 ">
          <div className="w-full sm:w-72">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2  ">
              Select Instructor:
            </label>
            <select
              value={instructor?.instructor_id || ""}
              onChange={handleInstructorChange}
              className="w-full p-2.5 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
            >
              {instructors.map((inst) => (
                <option key={inst.instructor_id} value={inst.instructor_id}>
                  {inst.first_name} {inst.last_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* INSTRUCTOR DETAILS */}
      {instructor ? (
        <div className="animate-fade-in">
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-6">
            <img
              src={
                instructor.instructor_image
                  ? `${SummaryApi.Images.url.replace("api", "")}${
                      instructor.instructor_image
                    }`
                  : instructorImage
              }
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = instructorImage;
              }}
              alt={`${instructor.first_name} ${instructor.last_name}`}
              className="w-full sm:w-[30%] md:w-[20%] rounded-xl border-2 border-gray-200 dark:border-gray-600 object-cover shadow-sm"
              style={{ minHeight: "200px", maxHeight: "300px" }}
            />

            <div className="flex-1 flex flex-col justify-start w-full">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">
                  {instructor.first_name} {instructor.last_name}
                </h2>
                <p className="text-md sm:text-lg font-medium text-blue-600 dark:text-blue-300 mb-3">
                  {instructor.job_title || "Instructor"}
                </p>

                <div className="text-gray-700 dark:text-gray-300 text-justify leading-relaxed space-y-3 text-sm sm:text-base">
                  <p>
                    {instructor.about_instructor1 ||
                      instructor.description ||
                      "No biography available for this instructor."}
                  </p>
                  {instructor.about_instructor2 && (
                    <p>{instructor.about_instructor2}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* SKILLS SECTION */}
          {instructor.skills && (
            <div className="mt-6 border-t border-gray-200 dark:border-gray-600 pt-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white mb-3">
                Key Skills & Expertise
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base pl-5">
                {parseSkills(instructor.skills).map((skill, idx) => (
                  <li key={idx} className="list-disc">
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 text-center border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-600 dark:text-gray-400">
            Select an instructor from the list above to view details.
          </p>
        </div>
      )}
    </div>
  </div>
)}

            {/* REVIEW */}
            {activeTab === "review" && (
              <div>
                <h2 className="bg-[#0F3557] dark:bg-gray-700 text-white font-bold text-lg sm:text-xl rounded-t-md p-3 sm:p-4 mb-3 sm:mb-4">
                  Reviews
                </h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base">
                  User reviews and ratings will be displayed here.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar – Course Information */}
        <div className="w-full md:w-96 bg-white dark:bg-gray-700 rounded-lg shadow flex flex-col h-auto">
          <h2 className="bg-[#0F3557] dark:bg-blue-700 text-white font-semibold text-base sm:text-lg rounded-t-md p-3 sm:p-4">
            Course Information
          </h2>

          <div className="p-4 sm:p-6">
            <div className="flex flex-col gap-3 text-sm mb-4 sm:mb-6">
              <div className="flex justify-between items-center">
                <span className="text-blue-700 dark:text-blue-300 font-medium">Level:</span>
                <span className="font-semibold text-gray-800 dark:text-white">{course.diffculty_level}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-blue-700 dark:text-blue-300 font-medium">Duration:</span>
                <span className="font-semibold text-gray-800 dark:text-white">{course.course_duration}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-blue-700 dark:text-blue-300 font-medium">Certificate:</span>
                <span className="font-semibold text-gray-800 dark:text-white">{course.course_title}</span>
              </div>
              <hr className="border-gray-300 dark:border-gray-600 mt-1" />
            </div>

            <div className="flex flex-col gap-3 mb-6 sm:mb-8 text-blue-700 dark:text-blue-300 pt-1">
              <FeatureItem text="24/7 Learning Support" />
              <FeatureItem text="Industry Expert Instructors" />
              <FeatureItem text="Hands-on Projects" />
              <FeatureItem text="Certificate of Completion" />
              <FeatureItem text="Lifetime Access" />
              <FeatureItem text="Mobile & Desktop Access" />
            </div>

            <div>
              <button
                className={`w-full rounded-md py-3 font-semibold mb-3 transition text-sm sm:text-base ${
                  isEnrolled
                    ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                    : " bg-[#1C3664]  text-white hover:bg-blue-800"
                }`}
                onClick={handleEnrollClick}
                disabled={isEnrolled}
              >
                {isEnrolled ? "Enrolled" : "Enroll Now"}
              </button>

              <button className="border w-full border-blue-400 dark:border-blue-600 text-blue-600 dark:text-blue-400 py-3 rounded-md mb-3 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition text-sm sm:text-base">
                Download Brochure
              </button>

              <button
                onClick={openCerticateModal}
                className="border w-full border-green-500 dark:border-green-600 text-green-600 dark:text-green-400 py-3 rounded-md font-semibold hover:bg-green-50 dark:hover:bg-green-900/30 flex justify-center items-center gap-2 transition text-sm sm:text-base"
              >
                <MdDownload className="w-4 h-4 sm:w-5 sm:h-5" />
                Preview Certificate
              </button>

              {/* Certificate Modal */}
              {isCertificateModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex justify-center items-center z-50 p-4">
                  <div className="bg-white dark:bg-gray-700 p-3 sm:p-4 rounded-md max-w-3xl w-full relative">
                    <button
                      onClick={closeCertificateModal}
                      className="absolute top-2 right-2 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white text-xl"
                    >
                      ×
                    </button>
                    <img
                      src={certificateImage}
                      alt="Certificate"
                      className="w-full h-auto rounded"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
{/* Slot Selection Modal */}
{isSlotModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex justify-center items-center z-50 p-4">
    <div className="bg-white dark:bg-gray-700 p-4 sm:p-6 rounded-md max-w-6xl w-full relative max-h-[90vh] overflow-y-auto">
      <button
        onClick={() => setIsSlotModalOpen(false)}
        className="absolute top-2 right-2 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white text-2xl"
      >
        ×
      </button>

      {/* Header */}
      <h2 className="text-xl sm:text-2xl font-bold text-blue-800 dark:text-blue-300 mb-4 sm:mb-6 text-center">
        Available Instructor Batches
      </h2>

      {/* Instructors and Slots Section */}
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        {/* Instructors List - Left Side (Reduced Width) */}
        <div className="lg:w-1/3">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4">Select Instructor</h3>
          <div className="space-y-3 sm:space-y-4 max-h-80 sm:max-h-96 overflow-y-auto">
            {instructors && instructors.length > 0 ? (
              instructors.map((instructorItem) => (
                <div
                  key={instructorItem.instructor_id}
                  className={`p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition ${
                    selectedInstructor?.instructor_id === instructorItem.instructor_id
                      ? 'bg-blue-100 dark:bg-blue-900 border-blue-500'
                      : 'bg-gray-50 dark:bg-gray-600 border-gray-300 dark:border-gray-500 hover:bg-blue-50 dark:hover:bg-blue-800'
                  }`}
                  onClick={() => {
                    // Set selected instructor
                    setSelectedInstructor(instructorItem);
                    // Also update the main instructor state for the tab display
                    setInstructor(instructorItem);
                    // Filter slots for this instructor
                    const filteredSlots = instructorSlotsAll.filter(
                      slot => slot.instructor_id === instructorItem.instructor_id
                    );
                    setInstructorSlots(filteredSlots);
                    setSelectedSlot(null);
                  }}
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    <img
                      src={
                        instructorItem.instructor_image
                          ? `${SummaryApi.Images.url.replace("api", "")}${instructorItem.instructor_image}`
                          : instructorImage
                      }
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = instructorImage;
                      }}
                      alt={instructorItem.first_name}
                      className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-full border-2"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white truncate">
                        {instructorItem.first_name} {instructorItem.last_name}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 truncate">{instructorItem.job_title}</p>
                      <div className="flex items-center gap-2 sm:gap-3 mt-1">
                        <span className="text-sm text-yellow-500">★ {instructorItem.rating || "4.5"}</span>
                        <span className="text-sm text-gray-500">|</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {instructorItem.experience || "5+ years"} exp
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 text-center py-6">No instructors available</p>
            )}
          </div>
        </div>

        {/* Time Slots - Right Side (Increased Width) */}
        <div className="lg:w-2/3">
          {selectedInstructor ? (
            <div className="mb-4">

              {/* Batches List */}
              <div className="flex flex-col gap-3 sm:gap-4 max-h-80 sm:max-h-96 overflow-y-auto">
                {(() => {
                  const now = new Date();
                  const filteredSlots = instructorSlots.filter((slot) => {
                    const startDate = new Date(slot.start_date);
                    const endDate = new Date(slot.end_date);
                    if (endDate < now) return false;

                    const dayNames = [
                      "sunday",
                      "monday",
                      "tuesday",
                      "wednesday",
                      "thursday",
                      "friday",
                      "saturday",
                    ];
                    const todayDay = dayNames[now.getDay()];
                    const todaySlotTime = slot[todayDay];

                    if (startDate.toDateString() === now.toDateString() && todaySlotTime) {
                      const [startTime] = todaySlotTime.split("-");
                      const [sh, sm] = startTime.split(":").map(Number);
                      const startDateTime = new Date(startDate);
                      startDateTime.setHours(sh, sm, 0, 0);
                      if (now > startDateTime) return false;
                    }

                    return startDate >= new Date(now.setHours(0, 0, 0, 0));
                  });

                  if (filteredSlots.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center text-center py-8 sm:py-12">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-20 h-20 sm:w-24 sm:h-24 text-gray-400 dark:text-gray-500 mb-3 sm:mb-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12h6m2 9H7a2 2 0 01-2-2V7a2 2 0 012-2h4l2-2h4a2 2 0 012 2v14a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 text-center font-medium">
                       Alreadys Batch Started No upcoming batches available for this instructor.
                        </p>
                      </div>
                    );
                  }

                  return filteredSlots.map((slot, idx) => {
                    const days = [
                      "sunday",
                      "monday",
                      "tuesday",
                      "wednesday",
                      "thursday",
                      "friday",
                      "saturday",
                    ];
                    const activeDays = days.filter(
                      (day) => slot[day] && slot[day] !== "N/A"
                    );

                    const formatDate = (dateStr) => {
                      const d = new Date(dateStr);
                      const day = String(d.getDate()).padStart(2, "0");
                      const month = String(d.getMonth() + 1).padStart(2, "0");
                      const year = d.getFullYear();
                      return `${day}-${month}-${year}`;
                    };

                    const formatTime = (timeStr) => {
                      if (!timeStr || timeStr === "N/A") return "N/A";
                      const [start, end] = timeStr.split("-");
                      const format = (t) => {
                        const [h, m] = t.split(":");
                        const date = new Date();
                        date.setHours(parseInt(h), parseInt(m));
                        return date.toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        });
                      };
                      return `${format(start)} - ${format(end)}`;
                    };

                    const isSelected = selectedSlot?.time_slot_id === slot.time_slot_id;
                    const handleClickSlot = (clickedSlot) => {
                      if (
                        selectedSlot &&
                        selectedSlot.time_slot_id === clickedSlot.time_slot_id
                      ) {
                        setSelectedSlot(null);
                      } else {
                        handleSlotSelection(clickedSlot);
                      }
                    };

                    const startDate = new Date(slot.start_date);
                    const now = new Date();
                    const isToday = startDate.toDateString() === now.toDateString();
                    let batchTag = null;

                    if (isToday) {
                      batchTag = (
                        <span className="ml-2 px-2 sm:px-3 py-1 rounded bg-green-500 text-white text-xs sm:text-sm font-medium">
                          Today Batch
                        </span>
                      );
                    } else if (startDate > now) {
                      batchTag = (
                        <span className="ml-2 px-2 sm:px-3 py-1 rounded bg-green-500 text-white text-xs sm:text-sm font-medium">
                          Upcoming Batch
                        </span>
                      );
                    }

                    return (
                      <div
                        key={idx}
                        onClick={() => handleClickSlot(slot)}
                        className={`cursor-pointer border-2 rounded-lg p-3 sm:p-4 shadow-sm transition ${
                          isSelected
                            ? "bg-green-100 dark:bg-green-900/40 border-green-500 dark:border-green-600 ring-2 ring-green-400"
                            : "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-800/50"
                        }`}
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between flex-wrap">
                            <span className="text-lg sm:text-xl text-blue-700 dark:text-blue-300 font-semibold">
                              {slot.batch_name}
                            </span>
                            {batchTag}
                          </div>

                 <span className="text-sm text-gray-700 dark:text-gray-300">
                            {`Start date: ${formatDate(slot.start_date)}`}
                          </span>





    <span className="text-sm text-gray-700 dark:text-gray-300">
                            {`End date: ${formatDate(slot.end_date)}`}
                          </span>












                 <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-1">
  {activeDays.map((day) => {
    const dayName = day.charAt(0).toUpperCase() + day.slice(1); // Capitalize
    return (
      <div key={day} className="flex items-center">
        <span className="font-medium w-24">
          {dayName} :
        </span>
        <span className="ml-2">
          {formatTime(slot[day])}
        </span>
      </div>
    );
  })}
</div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-8 sm:py-12">
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">Please select an instructor to view available batches</p>
            </div>
          )}
        </div>
      </div>

      {/* Centered Confirm Button */}
      <div className="flex justify-center mt-6 sm:mt-8">
        <button
          onClick={handleConfirmSlot}
          disabled={!selectedSlot}
          className={`px-8 sm:px-12 py-3 sm:py-4 rounded-lg font-semibold transition text-base sm:text-lg ${
            selectedSlot
              ? "bg-blue-800 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-600 shadow-lg"
              : "bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"
          }`}
        >
          Confirm Selection
        </button>
      </div>
    </div>
  </div>
)}

    {/* Payment Modal */}
    {isPaymentModalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex justify-center items-center z-50 p-4">
        <div className="bg-white dark:bg-gray-700 p-4 sm:p-6 rounded-md max-w-lg w-full relative">
          <button
            onClick={() => setIsPaymentModalOpen(false)}
            className="absolute top-2 right-2 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white text-xl"
          >
            ×
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-blue-800 dark:text-blue-300 mb-3 sm:mb-4 text-center">
            Complete Your Payment
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4 sm:mb-6 text-center text-sm sm:text-base">
            You are enrolling in:{" "}
            <span className="font-semibold">{course.course_title}</span>
            <br />
            Total amount: <span className="font-bold text-green-600 dark:text-green-400">
              {course.total_price}
            </span>{" "}
          </p>

          <div className="space-y-3 sm:space-y-4">
            {/* Payment Options - All now call handlePaymentAndNavigate */}
            <button
              className="w-full bg-indigo-600 text-white py-3 rounded-md font-semibold hover:bg-indigo-700 transition flex items-center justify-center gap-2 text-sm sm:text-base"
              onClick={handlePaymentAndNavigate}
            >
              <FaMapMarkerAlt /> Pay with Card
            </button>
            <button
              className="w-full bg-purple-600 text-white py-3 rounded-md font-semibold hover:bg-purple-700 transition flex items-center justify-center gap-2 text-sm sm:text-base"
              onClick={handlePaymentAndNavigate}
            >
              <FaEnvelope /> Pay with PayPal
            </button>
            <button
              className="w-full bg-green-600 text-white py-3 rounded-md font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2 text-sm sm:text-base"
              onClick={handlePaymentAndNavigate}
            >
              <FaPhone /> Google Pay
            </button>
          </div>

          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-4 sm:mt-6 text-center">
            Your enrollment will be finalized upon successful payment.
          </p>
        </div>
      </div>
    )}

{/* Preview Course Modal - FULLY FIXED FOR MOBILE */}
{isPreviewModalOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 dark:bg-opacity-80 px-4 pt-16 pb-6">
    {/* Backdrop */}
    <div 
      className="absolute inset-0" 
      onClick={closePreviewModal}
    />

    {/* Modal Card - Starts lower so header never covers it */}
    <div className="relative w-full max-w-4xl max-h-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden flex flex-col p-5">
      
      {/* Header with Title + Close Button (Always Visible) */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-1 flex justify-between items-center z-10">
        <h2 className="text-xl sm:text-2xl font-bold text-#0F3557 dark:text-white text-center flex-1">
          Preview: {course?.course_title}
        </h2>
        <button
          onClick={closePreviewModal}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center 
                     bg-gray-100 dark:bg-gray-700 rounded-full text-3xl 
                     text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 
                     transition shadow-lg"
        >
          ×
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-1 pt-2">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-4">
          Course Curriculum
        </h3>

        {topics.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-400 text-center py-8">
            No curriculum available for preview.
          </p>
        ) : (
          <div className="space-y-4">
            {topics.map((topic) => (
              <div key={topic.id} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <div
                  className="flex justify-between items-center bg-gray-50 dark:bg-gray-700 px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                  onClick={() => toggleTopic(topic.id)}
                >
                  <h4 className="font-semibold text-gray-800 dark:text-white">
                    {topic.title}
                    <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                      ({topic.totalDuration} mins)
                    </span>
                  </h4>
                  {topic.isOpen ? (
                    <FiChevronUp className="text-xl text-gray-600" />
                  ) : (
                    <FiChevronDown className="text-xl text-gray-600" />
                  )}
                </div>

                {topic.isOpen && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-1 space-y-3">
                    {topic.lessons.length > 0 ? (
                      topic.lessons.map((lesson) => (
                        <div
                          key={lesson.lesson_id}
                          className="flex justify-between items-center bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300">
                              {lesson.lesson_name}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {lesson.duration} mins
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-1">
                        No lessons in this topic.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Close Button at Bottom (Extra Safety for Mobile) */}
      <div className="bg-gray-50 dark:bg-gray-700 px-6 py-1 border-t border-gray-200 dark:border-gray-600 mt-5">
        <button
          onClick={closePreviewModal}
          className="w-full bg-[#0F3557] hover:bg-blue-800 text-white font-bold py-1 rounded-lg transition text-lg mt-5 dark:bg-blue-800"
        >
          Close Preview
        </button>
      </div>
    </div>
  </div>
)}
  </div>
);
};

export default CourseDetails;